4<!--
  ReviewAssignmentFilterDialog.vue
  Shared filter dialog for review assignments — used by both ReviewAssignments.vue
  and ReviewCalendarPage.vue. Reads/writes the shared `filters` ref from
  useReviewAssignments so both pages stay in sync.

  Changes are staged in localFilters and only applied when the user clicks Apply.
  Clear/X buttons reset the real filters to defaults via clearFilters().

  Props:
    modelValue — controls dialog visibility (v-model)
-->
<template>
  <BaseFilterDialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    title="Assignments Filter"
    @clear="clearFilters"
    @save="onSave"
  >
    <!-- Date range filter: setting "from" auto-sets "to" for single-day convenience -->
    <v-row density="compact" class="mb-3">
      <v-col cols="6">
        <v-text-field
          v-model="localFilters.dateFrom"
          label="Date from"
          type="date"
          clearable
          variant="outlined"
          density="comfortable"
          hide-details
          @update:model-value="onDateFromChange"
        ></v-text-field>
      </v-col>
      <v-col cols="6">
        <v-text-field
          v-model="localFilters.dateTo"
          label="Date to"
          type="date"
          clearable
          variant="outlined"
          density="comfortable"
          hide-details
        ></v-text-field>
      </v-col>
    </v-row>
    <!-- Location multi-select: options derived from loaded schedule entries -->
    <v-select
      v-model="localFilters.location"
      label="Location"
      :items="availableLocations"
      clearable
      multiple
      chips
      closable-chips
      variant="outlined"
      density="comfortable"
      hide-details
      class="mb-3"
    ></v-select>
    <!-- Platform single-select -->
    <v-select
      v-model="localFilters.platform"
      label="Platform"
      :items="availablePlatforms"
      clearable
      variant="outlined"
      density="comfortable"
      hide-details
      class="mb-3"
    ></v-select>
    <!-- Brand multi-select -->
    <v-select
      v-model="localFilters.brand"
      label="Brand"
      :items="availableBrands"
      clearable
      multiple
      chips
      closable-chips
      variant="outlined"
      density="comfortable"
      hide-details
      class="mb-3"
    ></v-select>
    <!-- User single-select -->
    <v-select
      v-model="localFilters.user_id"
      label="User"
      :items="reviewUsers"
      item-title="display"
      item-value="id"
      clearable
      variant="outlined"
      density="comfortable"
      hide-details
    ></v-select>
  </BaseFilterDialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import BaseFilterDialog from "@/components/BaseFilterDialog.vue";
import { useReviewAssignments } from "@/composables/useReviewAssignments.js";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
});
defineEmits(["update:modelValue"]);

// Shared state from the singleton composable — filters and data used by both pages
const { filters, assignmentEntries, reviewUsers } = useReviewAssignments();

// Local staging copy — form fields bind here; changes only apply on Apply
const localFilters = ref({ dateFrom: null, dateTo: null, location: [], platform: null, brand: [], user_id: null });

// When the dialog opens, deep-copy the current real filter state into the local ref
watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      localFilters.value = {
        dateFrom: filters.value.dateFrom,
        dateTo: filters.value.dateTo,
        location: filters.value.location ? [...filters.value.location] : [],
        platform: filters.value.platform,
        brand: filters.value.brand ? [...filters.value.brand] : [],
        user_id: filters.value.user_id,
      };
    }
  },
);

// Build unique sorted dropdown options from all loaded schedule entries
// Build unique sorted dropdown options from all loaded assignment entries
const availableLocations = computed(() => [...new Set(assignmentEntries.value.map((e) => e.location))].sort());
const availablePlatforms = computed(() => [...new Set(assignmentEntries.value.map((e) => e.platform))].sort());
const availableBrands    = computed(() => [...new Set(assignmentEntries.value.map((e) => e.brand))].sort());

// Reset all real filter fields to empty defaults (called by @clear)
function clearFilters() {
  filters.value = { dateFrom: null, dateTo: null, location: [], platform: null, brand: [], user_id: null };
}

// Apply: copy local staging state → real composable filters
function onSave() {
  Object.assign(filters.value, localFilters.value);
}

// Auto-set local "date to" when "date from" is set for convenient single-day selection
function onDateFromChange(val) {
  if (val && !localFilters.value.dateTo) {
    localFilters.value.dateTo = val;
  }
}
</script>
